import { activeStatuses, actorId, ec, ecId, inventoryId, ecLean, handBack, itemField, record, scalar, upper, type JanusCtx, type Row } from '../ability/context.js';
import { removeStatusFromRow, statusCasterId, statusItemId, statusRows, statusRemovalBefore, STATUS_IDS } from './expiry.js';
import { resolveRemovalSupport } from './removal-support.js';
import { floorBandShape } from '../inventory/lift-occupancy.js';
import { type RestShape } from '../inventory/floor-move.js';
import { SPEED } from '../movement/fall-check.js';
import { encounterField, posCoord } from '../../shared/array-types.js';
import { carrierHasTrigger } from '../ability/statuses/status-trigger-gate.js';
import { triggerFamilyPreDraw, runTriggerFamily } from '../ability/trigger-executor.js';
import { skipUnported } from '../unported.js';

/** Pure equivalent of the page list reader, including sparse and singleton PHP rosters. */
function rosterRows(value: unknown): Row[] {
  const row = record(value);
  const entries = Array.isArray(value) ? value : row
    ? Object.keys(row).every(key => /^\d+$/.test(key)) ? Object.values(row) : [row] : [];
  return entries.flatMap(value => { const row = record(value); return row ? [row] : []; });
}

export function transportInventories(encounter: Row): Row[] {
  return rosterRows(encounter.encounter_inventories_arr);
}

/** Inventory removal destinations, including exact caster-side copies, are outside the creature-member map. */
export function inventoryStatusTargets(encounter: Row, removals: ReadonlyArray<{ target: string }>): string[] {
  const ids = new Set(transportInventories(encounter).map(row => inventoryId(record(row))).filter(Boolean));
  const creatures = new Set(rosterRows(encounter.encounter_creatures_arr).map(ecId));
  const encounterId = upper(encounterField(encounter, 'encounter_id'));
  // Caster-side copies can hold exact removals for vehicles absent from the placement roster.
  // The receiver binds every such destination through its stored inventory root.
  return [...new Set(removals.map(row => upper(row.target)).filter(id => ids.has(id) || (id !== encounterId && !creatures.has(id))))];
}

export function isOperatorStatus(status: Row): boolean {
  const id = statusItemId(status);
  return id === STATUS_IDS.riding || id === STATUS_IDS.piloting;
}

export function isTransportStatus(status: Row): boolean {
  const id = statusItemId(status);
  return isOperatorStatus(status) || id === STATUS_IDS.ridden || id === STATUS_IDS.piloted;
}

/** Retire a mount/vehicle pair, including inventory vehicles and orphaned operators. */
export function removeTransportLink(ctx: JanusCtx, owner: Row, selected: Row): void {
  const primary = isOperatorStatus(selected), selectedId = statusItemId(selected);
  const pilot = selectedId === STATUS_IDS.piloting || selectedId === STATUS_IDS.piloted;
  const operatorItem = pilot ? STATUS_IDS.piloting : STATUS_IDS.riding;
  const peerItemId = primary ? (pilot ? STATUS_IDS.piloted : STATUS_IDS.ridden) : operatorItem;
  const ownerId = actorId(owner), casterId = statusCasterId(selected), connection = upper(scalar(selected.status_connected_id));
  const holders = [ctx.encounter, ...ctx.members.values(), ...transportInventories(ctx.encounter)];
  // Normal Pilot is self-cast; historical movement captures also carry reciprocal caster IDs.
  const peers = holders.filter(member => actorId(member) && actorId(member) !== ownerId).flatMap(member =>
    activeStatuses(ctx, member).filter(status => statusItemId(status) === peerItemId
      && upper(scalar(status.status_connected_id)) === connection
      && upper(scalar(status.status_target_id) ?? actorId(member)) === actorId(member)
      && Number(scalar(status.status_deleted) ?? 0) === 0
      && (primary ? statusCasterId(status) === ownerId && (casterId === ownerId || actorId(member) === casterId)
        : actorId(member) === casterId && (statusCasterId(status) === ownerId || statusCasterId(status) === casterId)))
      .map(status => ({ member, status })));
  if (pilot && primary) for (const status of statusRows(owner, 'caster')) {
    const target = upper(scalar(status.status_target_id));
    if (target && target !== ownerId && !holders.some(holder => actorId(holder) === target)
      && statusItemId(status) === peerItemId && statusCasterId(status) === ownerId
      && upper(scalar(status.status_connected_id)) === connection && Number(scalar(status.status_deleted) ?? 0) === 0)
      peers.push({ member: { inventory: { inventory_id: target } }, status });
  }
  const rider = primary ? owner : ctx.members.get(casterId);
  const pair = [{ member: owner, status: selected }, ...peers];
  for (const { status } of pair) {
    if (!/^[0-9A-F-]{36}$/.test(upper(status.status_id)) || status.tick_added || ctx.statusesAdded.includes(status as never))
      handBack('dismount:stored-link-not-held');
  }
  const riding = primary ? selected : peers[0]?.status;
  const losses = [{ owner, status: selected }, ...(!primary && rider && riding ? [{ owner: rider, status: riding }] : [])]
    .map(({ owner, status }) => {
      const event = { trigger: 'triggers_when_caster_loses_status_effect', statusItemId: statusItemId(status) };
      const lossArm = carrierHasTrigger(ctx, owner, event.trigger, 'dismount:loss-trigger', event.statusItemId);
      if (lossArm) triggerFamilyPreDraw(ctx, owner, event);
      return { owner, event, lossArm };
    });
  for (const { member, status } of pair) {
    const itemId = statusItemId(status);
    const before = statusRemovalBefore(status, actorId(member));
    ctx.statusesRemoved.push({ status_id: upper(status.status_id), status_item_id: itemId, target: actorId(member), before });
    for (const holder of [...holders, ...rosterRows(ctx.encounter.encounter_creatures_arr)])
      removeStatusFromRow(holder, upper(status.status_id));
  }
  // An automatic carrier retirement has no independent hook; an explicit removal keeps its primary loss hook.
  for (const { owner, event, lossArm } of losses)
    if (lossArm) runTriggerFamily(ctx, event, owner, null, [owner], false, undefined, true);
  if (!rider) {
    const name = scalar(ecLean(owner, 'encounter_creature_name')) ?? itemField(owner, 'item_name') ?? 'Creature';
    ctx.rows.push(skipUnported(`dismount:${name}:rider-placement-not-held`));
    return;
  }
  const riderId = ecId(rider);
  // Settle natural speed and support before searching beside the retired carrier.
  resolveRemovalSupport(ctx, riderId, riding ?? { status_item_id: operatorItem });
  if (Number(ecLean(rider, 'encounter_creature_dead') ?? 0) === 1 || upper(ecLean(rider, 'preferred_speed_type')) === SPEED.flying) return;
  const raw = encounterField(ctx.encounter, 'encounter_shape');
  let parsed: unknown;
  try { parsed = typeof raw === 'string' ? (raw === '' ? null : JSON.parse(raw)) : raw; }
  catch { handBack('dismount:shape-not-held'); }
  const shape = record(parsed) as RestShape | null;
  if ((parsed != null && !shape) || (shape && !Array.isArray(shape.boxes))) handBack('dismount:shape-not-held');
  const start = { x: posCoord(rider, 'x_pos') ?? 0, y: posCoord(rider, 'y_pos') ?? 0, z: posCoord(rider, 'z_pos') ?? 0 };
  const floor = shape?.boxes.length ? floorBandShape(shape, start.x, start.y, start.z)?.floor : 0;
  if (floor === undefined || Math.abs(floor - start.z) > 0.01) return; // aloft / object-top exits keep the support verdict
  const projected: Row = { ...ctx.encounter, encounter_creatures_arr: [...ctx.members.values()],
    encounter_inventories_arr: transportInventories(ctx.encounter) };
  if (!ctx.read.groundExitPlacement) handBack('dismount:placement-not-loaded');
  const result = ctx.read.groundExitPlacement!(projected, rider, start);
  if (result.status === 'unknown') handBack(`dismount:placement:${result.reason}`);
  if (result.status !== 'placed') return; // legacy already-clear / nowhere-clear keeps the rider's feet
  const updates = { x_pos: result.x, y_pos: result.y, z_pos: result.z };
  rider.position = { ...record(rider.position ?? rider.pos_arr), ...updates };
  Object.assign(ec(rider), updates);
}
